<script lang="ts">
  import ArrowClockwiseIcon from 'phosphor-svelte/lib/ArrowClockwiseIcon';
  import CheckCircleIcon from 'phosphor-svelte/lib/CheckCircleIcon';
  import DesktopTowerIcon from 'phosphor-svelte/lib/DesktopTowerIcon';
  import DotsThreeVerticalIcon from 'phosphor-svelte/lib/DotsThreeVerticalIcon';
  import KeyIcon from 'phosphor-svelte/lib/KeyIcon';
  import PencilSimpleIcon from 'phosphor-svelte/lib/PencilSimpleIcon';
  import TrashIcon from 'phosphor-svelte/lib/TrashIcon';
  import WarningCircleIcon from 'phosphor-svelte/lib/WarningCircleIcon';
  import { SvelteSet } from 'svelte/reactivity';

  import type { DeviceView, EncryptionStatusView } from '#src/generated/protocol';
  import { CoreError } from '#src/transport';
  import { useCoreClient } from '#lib/core/context.js';
  import { formatDate, formatTime } from '#lib/ui/date-time.js';
  import {
    openExternalAuthUrl,
    openExternalAuthWindow,
    type ExternalAuthWindow,
  } from '#lib/platform/external-auth.js';
  import { i18n, t } from '#lib/i18n.js';
  import ActionMenu from '#lib/ui/primitives/ActionMenu.svelte';
  import ActionMenuItem from '#lib/ui/primitives/ActionMenuItem.svelte';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import AppPageShell from '#lib/ui/primitives/AppPageShell.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import Label from '#lib/ui/primitives/Label.svelte';
  import LinkButton from '#lib/ui/primitives/LinkButton.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import StatusBadge from '#lib/ui/primitives/StatusBadge.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';
  import '#lib/ui/primitives/settings-row.css';
  import ResetIdentityDialog from './ResetIdentityDialog.svelte';
  import VerifyDeviceDialog from './VerifyDeviceDialog.svelte';
  import DeviceActionForm from './DeviceActionForm.svelte';
  import RoomKeyFile from './RoomKeyFile.svelte';
  import KeyBackupSettings from './KeyBackupSettings.svelte';

  const core = useCoreClient();
  const accountManagementUrl = core.oauthMetadata?.account_management_uri;
  let devices = $state.raw<DeviceView[]>([]);
  let status = $state<EncryptionStatusView | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let editing = $state<string | null>(null);
  let displayName = $state('');
  let password = $state('');
  let managingRecovery = $state(false);
  let newRecoveryKey = $state<string | null>(null);
  let confirmingReset = $state(false);
  let deleting = $state<string | null>(null);
  let removing = $state<string | null>(null);
  let verificationOpen = $state(false);
  let resettingIdentity = $state(false);
  let verifying = $state<string | null>(null);
  let currentDevice = $derived(devices.find((device) => device.is_own));
  let cancelled = false;
  let statusRevision = 0;
  let devicesRevision = 0;

  const bulkSelected = new SvelteSet<string>();
  let bulkRemoving = $state(false);
  let bulkBusy = $state(false);
  let bulkPassword = $state('');
  let selectableDevices = $derived(devices.filter((device) => !device.is_own));
  let allSelected = $derived(
    selectableDevices.length > 0 && bulkSelected.size === selectableDevices.length
  );

  function deviceName(device: DeviceView | undefined): string {
    return device?.display_name?.trim() || t('settings.unnamedDevice');
  }

  function missingSigningKeys(keys: EncryptionStatusView['signing_keys']): string[] {
    return [
      keys.master ? null : t('settings.signingKeyMaster'),
      keys.self_signing ? null : t('settings.signingKeySelf'),
      keys.user_signing ? null : t('settings.signingKeyUser'),
    ].filter((name): name is string => name !== null);
  }

  const verificationLabel = (value: EncryptionStatusView['verification']) =>
    value === 'verified'
      ? t('settings.verified')
      : value === 'unverified'
        ? t('settings.notVerified')
        : t('settings.unavailable');
  function messageFor(error: unknown): string {
    if (!(error instanceof CoreError)) return t('settings.actionFailed');
    if (error.detail.code === 'interactive_auth_required') return t('settings.enterPassword');
    if (error.detail.code === 'denied') return t('settings.wrongPassword');
    if (error.detail.code === 'unavailable') return t('settings.verificationUnavailable');
    return t('settings.actionFailed');
  }

  function recoveryMessageFor(error: unknown): string {
    if (error instanceof CoreError && error.detail.code === 'denied') {
      return t('settings.invalidRecoveryKey');
    }
    return messageFor(error);
  }

  async function refresh(): Promise<void> {
    const requestedStatus = statusRevision;
    const requestedDevices = devicesRevision;
    loading = true;
    error = null;
    try {
      const [nextStatus, nextDevices] = await Promise.all([
        core.rpc.encryption.status(),
        core.rpc.encryption.devices(),
      ]);
      if (cancelled) return;
      if (requestedStatus === statusRevision) status = nextStatus;
      if (requestedDevices === devicesRevision) devices = nextDevices.devices;
    } catch (cause) {
      if (!cancelled) error = messageFor(cause);
    } finally {
      if (!cancelled) loading = false;
    }
  }

  async function verifyDevice(deviceId: string): Promise<void> {
    if (verifying !== null) return;

    verifying = deviceId;
    error = null;
    try {
      await core.requestVerification(core.session?.user_id ?? '', deviceId);
    } catch (cause) {
      error = messageFor(cause);
    } finally {
      verifying = null;
    }
  }

  function beginRename(device: DeviceView): void {
    editing = device.device_id;
    displayName = device.display_name ?? '';
  }

  function cancelRename(): void {
    editing = null;
    displayName = '';
  }

  function cancelRemoval(): void {
    deleting = null;
    password = '';
  }

  async function saveName(deviceId: string): Promise<void> {
    try {
      await core.rpc.encryption.renameDevice({
        device_id: deviceId,
        display_name: displayName.trim(),
      });
      cancelRename();
      await refresh();
    } catch (cause) {
      error = recoveryMessageFor(cause);
    }
  }

  async function removeDevice(
    deviceId: string,
    authWindow: ExternalAuthWindow | null = null
  ): Promise<void> {
    if (removing !== null) return;
    removing = deviceId;
    error = null;
    try {
      const managementUrl = await core.rpc.encryption.deleteDevice({
        device_id: deviceId,
        password: password || null,
      });
      if (managementUrl) {
        if (authWindow) await authWindow.navigate(managementUrl);
        else await openExternalAuthUrl(managementUrl);
      } else {
        await refresh();
      }
      cancelRemoval();
    } catch (cause) {
      authWindow?.close();
      error = messageFor(cause);
    } finally {
      removing = null;
    }
  }

  function toggleSelected(deviceId: string): void {
    if (bulkSelected.has(deviceId)) bulkSelected.delete(deviceId);
    else bulkSelected.add(deviceId);
  }

  function toggleSelectAll(): void {
    bulkSelected.clear();
    if (!allSelected) {
      for (const device of selectableDevices) bulkSelected.add(device.device_id);
    }
  }

  function cancelBulkRemoval(): void {
    bulkRemoving = false;
    bulkPassword = '';
  }

  async function confirmBulkRemoval(): Promise<void> {
    const ids = [...bulkSelected];
    if (ids.length === 0 || bulkBusy) return;

    bulkBusy = true;
    error = null;
    const failed: string[] = [];
    for (const deviceId of ids) {
      try {
        const managementUrl = await core.rpc.encryption.deleteDevice({
          device_id: deviceId,
          password: accountManagementUrl ? null : bulkPassword || null,
        });
        if (managementUrl) await openExternalAuthUrl(managementUrl);
      } catch (cause) {
        console.warn('[sable settings] device removal failed', cause);
        failed.push(deviceName(devices.find((device) => device.device_id === deviceId)));
      }
    }

    bulkPassword = '';
    bulkBusy = false;
    bulkRemoving = false;
    bulkSelected.clear();
    await refresh();
    if (failed.length > 0) {
      error = t('settings.bulkRemoveDevicesFailed', { names: failed.join(', ') });
    }
  }

  function beginRemoval(deviceId: string): void {
    if (!accountManagementUrl) {
      deleting = deviceId;
      return;
    }

    const authWindow = openExternalAuthWindow(`sable-device-${crypto.randomUUID()}`);
    if (!authWindow) {
      error = t('settings.actionFailed');
      return;
    }
    void removeDevice(deviceId, authWindow);
  }

  async function manageRecovery(reset = false): Promise<void> {
    managingRecovery = true;
    error = null;
    try {
      newRecoveryKey = reset
        ? await core.rpc.encryption.resetRecoveryKey({ passphrase: null })
        : await core.rpc.encryption.enableRecovery({ passphrase: null });
      await refresh();
    } catch (cause) {
      error = messageFor(cause);
    } finally {
      managingRecovery = false;
    }
  }

  async function confirmReset(): Promise<void> {
    await manageRecovery(true);
    confirmingReset = false;
  }

  $effect(() => {
    void refresh();
    const unsubscribe = core.subscribeEvents((event) => {
      if (event.type === 'encryption_status') {
        statusRevision += 1;
        status = event.status;
      }
      if (event.type === 'devices_changed') {
        devicesRevision += 1;
        devices = event.devices;
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  });
</script>

{#snippet refreshAction()}
  <IconButton
    variant="subtle"
    size="small"
    label={i18n.t('common.refresh')}
    onclick={refresh}
    disabled={loading}
  >
    <ArrowClockwiseIcon />
  </IconButton>
{/snippet}

<AppPageShell
  maxWidth="56rem"
  title={i18n.t('settings.devicesTitle')}
  density="compact"
  class="devices-settings"
>
  {#if error}<Alert class="settings-error" variant="critical" role="alert">{error}</Alert>{/if}

  {#if newRecoveryKey}
    <Alert class="recovery-key" variant="warning" role="status">
      <div class="recovery-key-heading">
        <KeyIcon aria-hidden="true" />
        <div>
          <strong>{i18n.t('settings.saveRecoveryKey')}</strong>
          <p>{i18n.t('settings.saveRecoveryKeyDescription')}</p>
        </div>
      </div>
      <code>{newRecoveryKey}</code>
      <Button onclick={() => (newRecoveryKey = null)}>{i18n.t('settings.savedRecoveryKey')}</Button>
    </Alert>
  {/if}

  <div class="settings-stack" aria-busy={loading}>
    <SettingsSection headingId="encryption-heading" title={i18n.t('settings.encryption')}>
      {#if status}
        <div class="status-grid">
          <div class="status-item">
            <span
              class={['status-icon', { positive: status.verification === 'verified' }]}
              aria-hidden="true"
            >
              {#if status.verification === 'verified'}<CheckCircleIcon
                  weight="fill"
                />{:else}<WarningCircleIcon />{/if}
            </span>
            <div class="status-copy">
              <div class="device-name">{deviceName(currentDevice)}</div>
              <div class="status-badge-row">
                <StatusBadge
                  variant={status.verification === 'verified'
                    ? 'success'
                    : status.verification === 'unverified'
                      ? 'warning'
                      : 'neutral'}
                  label={verificationLabel(status.verification)}
                />
              </div>
            </div>
            {#if status.verification !== 'verified' || (status.recovery === 'incomplete' && !status.backup_unlocked)}
              <Button variant="primary" onclick={() => (verificationOpen = true)}>
                {i18n.t(
                  status.verification === 'verified'
                    ? 'settings.recoveryIncompleteAction'
                    : 'settings.verifyDevice'
                )}
              </Button>
            {/if}
          </div>
        </div>

        {#if status.verification === 'verified' && status.recovery === 'incomplete' && status.backup_unlocked}
          <div class="setting-row">
            <span class="row-icon" aria-hidden="true"><KeyIcon /></span>
            <div class="row-copy">
              <strong>{i18n.t('settings.signingKeysMissingTitle')}</strong>
              <p>
                {i18n.t('settings.signingKeysMissingBody', {
                  keys: missingSigningKeys(status.signing_keys).join(', '),
                })}
              </p>
            </div>
            <Button variant="secondary" onclick={() => (verificationOpen = true)}>
              {i18n.t('settings.signingKeysFetch')}
            </Button>
          </div>
        {/if}

        {#if status.verification === 'verified' && status.recovery !== 'incomplete'}
          <div class="setting-row">
            <span class="row-icon" aria-hidden="true"><KeyIcon /></span>
            <div class="row-copy">
              <strong
                >{i18n.t(
                  status.recovery === 'enabled'
                    ? 'settings.resetRecoveryKey'
                    : 'common.setUpRecovery'
                )}</strong
              >
              <p>
                {i18n.t(
                  status.recovery === 'enabled'
                    ? 'settings.resetRecoveryKeyDescription'
                    : 'settings.setUpRecoveryDescription'
                )}
              </p>
            </div>
            <Button
              variant={status.recovery === 'enabled' ? 'secondary' : 'primary'}
              loading={managingRecovery}
              onclick={() => {
                if (status?.recovery === 'enabled') confirmingReset = true;
                else void manageRecovery();
              }}
            >
              {i18n.t(
                status.recovery === 'enabled' ? 'settings.resetRecoveryKey' : 'common.setUpRecovery'
              )}
            </Button>
          </div>
        {/if}

        <div class="setting-row">
          <span class="row-icon" aria-hidden="true"><WarningCircleIcon /></span>
          <div class="row-copy">
            <strong>{i18n.t('settings.resetIdentity')}</strong>
            <p>{i18n.t('settings.resetIdentityDescription')}</p>
          </div>
          <Button variant="danger" onclick={() => (resettingIdentity = true)}>
            {i18n.t('settings.resetIdentity')}
          </Button>
        </div>
      {:else if loading}
        <div class="settings-form loading-state" role="status">
          <Spinner /><span>{i18n.t('settings.loadingEncryption')}</span>
        </div>
      {/if}
    </SettingsSection>

    <KeyBackupSettings onUnlock={() => (verificationOpen = true)} />

    <SettingsSection
      headingId="devices-heading"
      title={i18n.t('settings.signedInDevices')}
      titleActions={refreshAction}
    >
      {#snippet actions()}
        {#if accountManagementUrl}
          {@const url = accountManagementUrl}
          <LinkButton
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onclick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void openExternalAuthUrl(url).catch((cause: unknown) => {
                error = messageFor(cause);
              });
            }}
          >
            {i18n.t('settings.devicesDashboard')}
          </LinkButton>
        {/if}
      {/snippet}
      {#if loading}
        <div class="settings-form loading-state" role="status">
          <Spinner /><span>{i18n.t('settings.loadingDevices')}</span>
        </div>
      {:else if devices.length === 0}
        <div class="settings-form empty-state">
          <DesktopTowerIcon />
          <p>{i18n.t('settings.noDevices')}</p>
        </div>
      {:else}
        {#if selectableDevices.length > 0}
          <div class="bulk-bar">
            <label class="bulk-select-all">
              <input
                type="checkbox"
                checked={allSelected}
                disabled={bulkBusy}
                onchange={toggleSelectAll}
              />
              {i18n.t('common.selectAll')}
            </label>
            {#if bulkSelected.size > 0}
              <span class="bulk-count"
                >{i18n.t('settings.devicesSelectedCount', { count: bulkSelected.size })}</span
              >
              <Button
                variant="danger"
                size="small"
                disabled={bulkBusy}
                onclick={() => {
                  bulkRemoving = true;
                }}
              >
                {i18n.t('settings.removeSelectedDevices')}
              </Button>
            {/if}
          </div>
        {/if}

        {#if bulkRemoving}
          <form
            class="bulk-remove-form"
            onsubmit={(event) => {
              event.preventDefault();
              void confirmBulkRemoval();
            }}
          >
            <div class="row-copy">
              <strong
                >{i18n.t('settings.removeSelectedDevicesConfirm', {
                  count: bulkSelected.size,
                })}</strong
              >
              <p>{i18n.t('settings.removeDeviceDescription')}</p>
            </div>
            {#if !accountManagementUrl}
              <Label for="bulk-device-password">{i18n.t('settings.password')}</Label>
              <TextInput
                id="bulk-device-password"
                type="password"
                bind:value={bulkPassword}
                autocomplete="current-password"
                autofocus
              />
            {/if}
            <div class="form-actions">
              <Button type="submit" variant="danger" loading={bulkBusy}>
                {i18n.t('settings.removeSelectedDevices')}
              </Button>
              <Button variant="ghost" disabled={bulkBusy} onclick={cancelBulkRemoval}>
                {i18n.t('common.cancel')}
              </Button>
            </div>
          </form>
        {/if}

        <ul class="device-list">
          {#each devices as device (device.device_id)}
            <li class="device">
              <div class="device-summary">
                {#if device.is_own}
                  {#if selectableDevices.length > 0}<span class="device-select"></span>{/if}
                {:else}
                  <label class="device-select">
                    <input
                      type="checkbox"
                      checked={bulkSelected.has(device.device_id)}
                      disabled={bulkBusy}
                      aria-label={i18n.t('settings.selectDevice', { name: deviceName(device) })}
                      onchange={() => {
                        toggleSelected(device.device_id);
                      }}
                    />
                  </label>
                {/if}
                <span class="device-icon" aria-hidden="true"><DesktopTowerIcon /></span>
                <div class="device-info">
                  <div class="device-name-line">
                    <span class="device-name">{deviceName(device)}</span>
                    {#if device.is_own}<StatusBadge
                        variant="primary"
                        label={i18n.t('settings.currentDevice')}
                      />{/if}
                  </div>
                  <div class="device-meta">
                    <StatusBadge
                      variant={!device.has_keys
                        ? 'neutral'
                        : device.is_verified
                          ? 'success'
                          : 'warning'}
                      label={!device.has_keys
                        ? i18n.t('settings.noDeviceKeys')
                        : device.is_verified
                          ? i18n.t('settings.verified')
                          : i18n.t('settings.notVerified')}
                    />
                    <Tooltip label={device.device_id} multiline>
                      {#snippet trigger({ props })}
                        <code {...props}>{device.device_id}</code>
                      {/snippet}
                    </Tooltip>
                  </div>
                  {#if device.last_seen_ts !== null}
                    <span class="device-seen">
                      {i18n.t('settings.deviceLastActivity', {
                        when: `${formatDate(device.last_seen_ts)} ${formatTime(device.last_seen_ts)}`,
                      })}
                    </span>
                  {/if}
                  {#if !device.is_own && device.has_keys && !device.is_verified && status?.verification === 'verified'}
                    <div class="device-verify">
                      <Button
                        variant="secondary"
                        size="small"
                        loading={verifying === device.device_id}
                        onclick={() => {
                          void verifyDevice(device.device_id);
                        }}
                      >
                        {i18n.t('settings.verifyDevice')}
                      </Button>
                    </div>
                  {/if}
                </div>
                {#if editing !== device.device_id && deleting !== device.device_id}
                  <div class="device-actions">
                    {#if device.is_own}
                      <IconButton
                        variant="ghost"
                        size="small"
                        label={i18n.t('settings.renameDevice', { name: deviceName(device) })}
                        onclick={() => {
                          beginRename(device);
                        }}><PencilSimpleIcon /></IconButton
                      >
                    {:else}
                      <ActionMenu
                        label={i18n.t('settings.deviceOptions', { name: deviceName(device) })}
                      >
                        {#snippet trigger({ props })}
                          <IconButton
                            {...props}
                            variant="ghost"
                            size="small"
                            label={i18n.t('settings.deviceOptions', { name: deviceName(device) })}
                            ><DotsThreeVerticalIcon /></IconButton
                          >
                        {/snippet}
                        <ActionMenuItem
                          onSelect={() => {
                            beginRename(device);
                          }}
                        >
                          <PencilSimpleIcon aria-hidden="true" />
                          {i18n.t('settings.rename')}
                        </ActionMenuItem>
                        <ActionMenuItem
                          destructive
                          onSelect={() => {
                            beginRemoval(device.device_id);
                          }}
                        >
                          <TrashIcon aria-hidden="true" />
                          {i18n.t('common.remove')}
                        </ActionMenuItem>
                      </ActionMenu>
                    {/if}
                  </div>
                {/if}
              </div>

              {#if editing === device.device_id}
                <DeviceActionForm
                  mode="rename"
                  deviceId={device.device_id}
                  bind:displayName
                  onSubmit={() => void saveName(device.device_id)}
                  onCancel={cancelRename}
                />
              {/if}

              {#if deleting === device.device_id}
                <DeviceActionForm
                  mode="remove"
                  deviceId={device.device_id}
                  accountManagement={accountManagementUrl !== undefined}
                  bind:password
                  busy={removing === device.device_id}
                  onSubmit={() => void removeDevice(device.device_id)}
                  onCancel={cancelRemoval}
                />
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </SettingsSection>

    <RoomKeyFile />
  </div>
</AppPageShell>

<ConfirmDialog
  bind:open={confirmingReset}
  title={i18n.t('settings.resetRecoveryKeyConfirmTitle')}
  description={i18n.t('settings.resetRecoveryKeyConfirmDescription')}
  confirmLabel={i18n.t('settings.resetRecoveryKey')}
  busy={managingRecovery}
  onConfirm={() => void confirmReset()}
/>

{#if status}
  <VerifyDeviceDialog
    bind:open={verificationOpen}
    recovery={status.recovery}
    recoveryPassphrase={status.recovery_passphrase}
    onVerified={refresh}
    onReset={() => (resettingIdentity = true)}
  />
{/if}

<ResetIdentityDialog bind:open={resettingIdentity} onReset={refresh} />

<style>
  .device-seen {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
  }

  .settings-stack {
    display: grid;
    gap: var(--space-300);
  }

  .status-grid {
    display: grid;
    grid-template-columns: 1fr;
    margin: 0;
  }

  .status-item {
    align-items: center;
    border-bottom: var(--border-width) solid var(--bg-container-line);
    display: flex;
    gap: var(--space-300);
    min-width: 0;
    padding: var(--space-400);
  }

  .status-item:last-child {
    border-bottom: 0;
  }

  .status-copy {
    flex: 1;
    min-width: 0;
  }

  .status-icon,
  .row-icon,
  .device-icon {
    align-items: center;
    background: var(--surface-container);
    border-radius: var(--radius);
    color: var(--surface-on-container);
    display: flex;
    flex: 0 0 auto;
    height: var(--control-height-small);
    justify-content: center;
    width: var(--control-height-small);
  }

  .status-icon.positive {
    background: var(--success-container);
    color: var(--success-on-container);
  }

  .status-icon :global(svg),
  .row-icon :global(svg),
  .device-icon :global(svg) {
    height: var(--icon-size-small);
    width: var(--icon-size-small);
  }

  .device-name {
    font-weight: var(--font-weight-medium);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status-badge-row {
    margin: var(--space-100) 0 0;
  }

  .setting-row {
    align-items: stretch;
    border-top: var(--border-width) solid var(--bg-container-line);
    display: flex;
    flex-direction: column;
    gap: var(--space-400);
    padding: var(--space-300) var(--space-400);
  }

  .setting-row > :global(.btn) {
    width: 100%;
  }

  .row-copy {
    flex: 1;
    min-width: 0;
  }

  .row-copy p {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin: var(--space-100) 0 0;
  }

  .bulk-bar {
    align-items: center;
    border-bottom: var(--border-width) solid var(--bg-container-line);
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-300);
    padding: var(--space-300) var(--space-400);
  }

  .bulk-select-all {
    align-items: center;
    display: flex;
    gap: var(--space-200);
  }

  .bulk-count {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
  }

  .bulk-remove-form {
    background: var(--surface-container);
    border-bottom: var(--border-width) solid var(--bg-container-line);
    color: var(--surface-on-container);
    display: grid;
    gap: var(--space-300);
    padding: var(--space-300) var(--space-400);
  }

  .form-actions {
    display: flex;
    gap: var(--space-200);
  }

  .device-select {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    inline-size: var(--icon-size-small);
    justify-content: center;
    position: relative;
  }

  .device-select::after {
    content: '';
    inset: calc((var(--icon-size-small) - var(--target-hit)) / 2);
    position: absolute;
  }

  .device-select input {
    block-size: var(--icon-size-small);
    inline-size: var(--icon-size-small);
    margin: 0;
  }

  .device-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .device + .device {
    border-top: var(--border-width) solid var(--bg-container-line);
  }

  .device-summary {
    align-items: center;
    display: flex;
    gap: var(--space-300);
    min-height: calc(var(--control-height-medium) + var(--space-300));
    padding: var(--space-300) var(--space-400);
  }

  .device-info {
    display: grid;
    flex: 1;
    gap: var(--space-150);
    grid-template-columns: minmax(0, 1fr);
    min-width: 0;
  }

  .device-name-line,
  .device-meta {
    min-width: 0;
  }

  .device-name-line :global(.status-badge),
  .device-meta :global(.status-badge) {
    flex: 0 0 auto;
  }

  .device-verify {
    display: flex;
  }

  .device-name-line,
  .device-meta,
  .device-actions,
  .recovery-key-heading {
    align-items: center;
    display: flex;
    gap: var(--space-200);
  }

  .device-meta code {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .device-actions {
    flex: 0 0 auto;
    gap: var(--space-100);
  }

  .loading-state,
  .empty-state {
    align-items: center;
    color: var(--surface-var-on-container);
    display: flex;
    gap: var(--space-300);
    justify-content: center;
    min-height: 5rem;
  }

  .empty-state {
    flex-direction: column;
  }

  .empty-state :global(svg) {
    height: var(--icon-size-large);
    width: var(--icon-size-large);
  }

  .empty-state p {
    margin: 0;
  }

  :global(.settings-error),
  :global(.recovery-key) {
    margin-bottom: var(--space-400);
  }

  :global(.alert.recovery-key) {
    gap: var(--space-300);
    padding: var(--space-400);
  }

  .recovery-key-heading :global(svg) {
    flex: 0 0 auto;
    height: var(--icon-size-medium);
    width: var(--icon-size-medium);
  }

  :global(.recovery-key) code {
    background: var(--bg-container);
    border: var(--border-width) solid var(--warn-container-line);
    border-radius: var(--radius);
    color: var(--bg-on-container);
    display: block;
    overflow-wrap: anywhere;
    padding: var(--space-300);
    user-select: all;
  }

  @media (width >= 42rem) {
    .status-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .status-item {
      border-bottom: 0;
    }

    .setting-row {
      align-items: center;
      flex-direction: row;
    }

    .setting-row > :global(.btn) {
      width: auto;
    }
  }
</style>
